import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError, get, withQuery } from "./client";

/** `/api/v1/reports/redlist`: the client's Red List progress report -- the
 * cards, the status breakdown, each contractor's load, the daily schedule and
 * the tracker rows -- read off the register, so the screen and the workbook at
 * `/reports/redlist.xlsx` say the same thing. */

export const WORK_STATUSES = [
  "Date Set",
  "To Be Confirmed",
  "On Hold - Issue",
  "BOQ Pending",
  "QCP Specs Done",
  "QCP Needed",
  "Completed",
] as const;
export type WorkStatus = (typeof WORK_STATUSES)[number];
export type Card = "Completed" | "Date Set" | "No Date Yet";

export interface RedListCards {
  total: number;
  completed: number;
  still_open: number;
  date_set: number;
  confirmed: number;
  awaiting: number;
  no_date_yet: number;
  visited: number;
  /** Completed over total, 0..1. */
  completion: number;
}

export interface BreakdownLine {
  work_status: WorkStatus;
  card: Card;
  orders: number;
  share: number;
  visited: number;
  visited_share: number;
}

export interface WorkloadLine {
  contractor: string;
  date_set: number;
  confirmed: number;
  awaiting: number;
  visited: number;
}

export interface ScheduleDay {
  day: string;
  proposed: Record<string, number>;
  unassigned: number;
  total: number;
  confirmed: number;
  lead_vs_target: string;
}

export interface TrackerRow {
  number: number;
  site_id: number;
  priority: number | null;
  work_order: string | null;
  site_name: string;
  address: string | null;
  func_loc: string | null;
  equip_no: string | null;
  contractor: string | null;
  work_status: WorkStatus;
  card: Card;
  proposed_date: string | null;
  confirmed_date: string | null;
  site_visited: boolean;
  schedule_status: string;
  date_change_days: number | null;
  finish_date: string | null;
  days_overdue: number | null;
  comments: string | null;
  plan_name: string | null;
}

export interface RedListReport {
  today: string;
  source: string;
  target_per_day: number;
  contractors: string[];
  cards: RedListCards;
  breakdown: BreakdownLine[];
  attention: Record<string, number>;
  workload: WorkloadLine[];
  days: ScheduleDay[];
  rows: TrackerRow[];
  options: {
    work_statuses: { value: WorkStatus; card: Card }[];
    schedule_statuses: string[];
    not_assigned: string;
  };
  urls: { xlsx: string };
}

export function redListReportUrl(target: number | null): string {
  return withQuery("/api/v1/reports/redlist", { target });
}

export function useRedListReport(target: number | null) {
  const url = redListReportUrl(target);
  return useQuery<RedListReport, ApiError>({
    queryKey: ["reports", "redlist", url],
    queryFn: () => get<RedListReport>(url),
    placeholderData: keepPreviousData,
  });
}

/** What the sheet writes as a percentage: whole points, "0%" for nothing. */
export function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}
